/*
  KG gift auto-add runner — one engine for every gift-with-purchase context
  (seasonal edition, referral introduction, permanent gifts). Replaces the
  per-context watcher scripts that previously lived inline in
  seasonal-gift-autoadd / referral-gift-autoadd; those snippets are now pure
  Liquid emitters of offer blobs this runner consumes.

  Offer blobs: every script[type="application/json"][data-kg-gift-offers]
  holds { "offers": [...] }. Offer shape:
    tag                    line-item property key marking our line (e.g. _kg_gift)
    property_value         value stored under the tag (edition slug / offer handle)
    flag                   token written to the added/declined attribute lists —
                           legacy-compatible: seasonal uses the variant id,
                           referral uses "1", permanent uses the offer handle
    added_attr             cart attribute holding the comma list of added flags
    declined_attr          cart attribute holding the comma list of declined flags
    gift_variant_id        the free SKU
    qualifying_product_ids any-of trigger
    requires_selling_plan  when true the qualifying line must also carry a
                           selling plan, so the gift belongs to the subscription
                           and not to a one-off buy of the same product. When such
                           an offer qualifies, any other live offer for the same
                           variant stands aside for that cart (see
                           supersededByPlanGift), so a joiner never receives an
                           edition's copy of the gift he already has
    gate_ref               null, or a ?ref handle that must be in session for
                           the offer to be live (referral)

  Pricing is never this file's job: the discount Function (seasonal + permanent
  100% rules) or the ShopJar BXGY code (referral) zeroes the line at checkout.

  State contract for the What-is-included widget: after every reconcile pass a
  `kg:gifts-synced` event fires with detail { cart, statuses } where statuses
  maps String(gift_variant_id) -> 'in' | 'declined' | 'absent'. The last detail
  is kept on window.kgGiftSyncState for late binders.
*/
(function () {
  'use strict';

  var CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  var RECONCILE_DEBOUNCE_MS = 150;
  var FAILED_ADD_BACKOFF_MS = 5 * 60 * 1000;
  var SESSION_KEY = 'kg_ref';

  var offers = readOffers();
  if (!offers.length) return;

  captureReferralContext(offers);

  // Private reference so our own cart writes bypass the wrapper and don't loop.
  var nativeFetch = window.fetch.bind(window);
  var reconciling = false;
  var pendingReconcile = false;
  var debounceTimer = null;

  function readOffers() {
    var out = [];
    var blobs = document.querySelectorAll('script[type="application/json"][data-kg-gift-offers]');
    Array.prototype.forEach.call(blobs, function (el) {
      try {
        var parsed = JSON.parse(el.textContent);
        if (parsed && parsed.offers) {
          parsed.offers.forEach(function (offer) {
            if (offer && offer.gift_variant_id && offer.tag) out.push(offer);
          });
        }
      } catch (err) {
        console.error('[kg-gift] offer blob is not valid JSON', el.id || el, err);
      }
    });
    return out;
  }

  // The ?ref param disappears on navigation; persist it the moment any gated
  // offer's handle matches (previously done by the referral snippet).
  function captureReferralContext(allOffers) {
    try {
      var ref = new URLSearchParams(window.location.search).get('ref');
      if (!ref) return;
      var gated = allOffers.some(function (o) { return o.gate_ref === ref; });
      if (gated) sessionStorage.setItem(SESSION_KEY, ref);
    } catch (err) { /* storage unavailable — referral context simply won't persist */ }
  }

  function gateActive(offer) {
    if (!offer.gate_ref) return true;
    try { return sessionStorage.getItem(SESSION_KEY) === offer.gate_ref; }
    catch (err) { return false; }
  }

  function getCart() {
    return nativeFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('GET /cart.js failed: ' + res.status);
      return res.json();
    });
  }

  function cartWrite(path, body) {
    return nativeFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (j) { throw new Error('POST ' + path + ' ' + res.status + ': ' + JSON.stringify(j)); });
      return res.json();
    });
  }

  // Whose line is it? The tag alone cannot say: every permanent gift is tagged
  // _kg_pgift, so two offers that hand out the SAME variant — the Ionic and the
  // Corinthian welcome gifts are both a Blade Bank — each recognised the other's
  // line. The offer that did not qualify then removed the one that did, and the
  // runner added and removed the gift on every pass. The property value is the
  // offer handle precisely so a line can name its owner; match on it.
  function isGiftLine(item, offer) {
    return hasGiftTag(item, offer) && item.properties[offer.tag] === offer.property_value;
  }

  // Looser: is this line ANY gift line, whoever owns it? Used to stop a gift
  // qualifying for another gift, where a stale or unknown value must still
  // count, so this one stays on the tag.
  function hasGiftTag(item, offer) {
    return !!(item.properties && item.properties[offer.tag]);
  }

  function isAnyGiftLine(item) {
    return offers.some(function (offer) { return hasGiftTag(item, offer); });
  }

  // Qualification is product-level, but a gift that belongs to a subscription
  // (permanent gifts carrying requires_selling_plan) must not land in a one-off
  // cart: a ritual set is the same product either way, so the qualifying line
  // has to be a subscription line as well as the right product.
  function cartQualifies(cart, offer) {
    var ids = offer.qualifying_product_ids || [];
    return cart.items.some(function (item) {
      if (isAnyGiftLine(item)) return false;
      if (ids.indexOf(item.product_id) === -1) return false;
      if (offer.requires_selling_plan && !item.selling_plan_allocation) return false;
      return true;
    });
  }

  // Two live offers can hand out the same variant: an edition gives the Blade
  // Bank to every set buyer while the Ionic welcome gift gives it to the joiner.
  // The subscription's gift owns the variant for that cart, so the edition offer
  // stands aside; the discount Function credits the edition's value on the
  // qualifying line instead (shopify-seasonal-creator config-contract, § Edition
  // credit). Data-driven: nothing here names an edition or a tier.
  function supersededByPlanGift(cart, offer) {
    if (offer.requires_selling_plan) return false;
    return offers.some(function (other) {
      return other !== offer &&
        other.gift_variant_id === offer.gift_variant_id &&
        other.requires_selling_plan &&
        gateActive(other) &&
        cartQualifies(cart, other);
    });
  }

  function offerIsLive(cart, offer) {
    return gateActive(offer) && cartQualifies(cart, offer) && !supersededByPlanGift(cart, offer);
  }

  function findGiftLine(cart, offer) {
    return cart.items.find(function (item) {
      return isGiftLine(item, offer) && item.variant_id === offer.gift_variant_id;
    }) || null;
  }

  function attrList(cart, key) {
    var raw = (cart.attributes && cart.attributes[key]) || '';
    return String(raw).split(',').filter(function (x) { return x; });
  }

  function attrHasFlag(cart, offer, key) {
    return attrList(cart, key).indexOf(String(offer.flag)) !== -1;
  }

  function writeAttr(key, value) {
    var attributes = {};
    attributes[key] = value;
    return cartWrite('/cart/update.js', { attributes: attributes });
  }

  function setFlag(cart, offer, key) {
    var flags = attrList(cart, key);
    flags.push(String(offer.flag));
    return writeAttr(key, flags.join(','));
  }

  function clearFlag(cart, offer, key) {
    var flags = attrList(cart, key).filter(function (x) { return x !== String(offer.flag); });
    return writeAttr(key, flags.join(','));
  }

  function addGift(offer) {
    var properties = {};
    properties[offer.tag] = offer.property_value;
    properties._r_gwp_hide_qty = 'true';
    return cartWrite('/cart/add.js', {
      items: [{ id: offer.gift_variant_id, quantity: 1, properties: properties }]
    }).catch(function (err) {
      // Out-of-stock (422) or transient failure: back off so we don't hammer.
      offer._failedUntil = Date.now() + FAILED_ADD_BACKOFF_MS;
      throw err;
    });
  }

  // One pass decides the single next action for one offer; the runner loops
  // until no action remains, so each pass only needs to progress one step.
  function decideAction(cart, offer) {
    var qualifies = offerIsLive(cart, offer);
    var line = findGiftLine(cart, offer);
    var added = attrHasFlag(cart, offer, offer.added_attr);
    var declined = attrHasFlag(cart, offer, offer.declined_attr);

    if (!qualifies) {
      if (line) return function () { return cartWrite('/cart/change.js', { id: line.key, quantity: 0 }); };
      if (added) return function () { return clearFlag(cart, offer, offer.added_attr); };
      if (declined) return function () { return clearFlag(cart, offer, offer.declined_attr); };
      return null;
    }
    if (line) {
      if (!added) return function () { return setFlag(cart, offer, offer.added_attr); };
      return null;
    }
    if (declined) return null;
    if (added) return function () { return setFlag(cart, offer, offer.declined_attr); };
    if (offer._failedUntil && Date.now() < offer._failedUntil) return null;
    return function () { return addGift(offer); };
  }

  // A line one of our tags claims, that no live offer recognises: a retired
  // edition's gift, a disabled permanent gift, a value from an older emitter.
  // Nothing zeroes it any more, so leaving it in place charges the customer
  // full price for something the page called a gift. Matching on the tag alone
  // used to sweep these away as a side effect; now it is deliberate.
  function findOrphanGiftLine(cart) {
    return cart.items.find(function (item) {
      return isAnyGiftLine(item) && !offers.some(function (offer) { return isGiftLine(item, offer); });
    }) || null;
  }

  function applyFirstAction(cart) {
    var orphan = findOrphanGiftLine(cart);
    if (orphan) {
      return cartWrite('/cart/change.js', { id: orphan.key, quantity: 0 }).then(function () { return true; });
    }
    for (var i = 0; i < offers.length; i++) {
      var action = decideAction(cart, offers[i]);
      if (action) return action().then(function () { return true; });
    }
    return Promise.resolve(false);
  }

  function statusFor(cart, offer) {
    if (findGiftLine(cart, offer)) return 'in';
    if (attrHasFlag(cart, offer, offer.declined_attr)) return 'declined';
    return 'absent';
  }

  function announceState(cart) {
    var statuses = {};
    offers.forEach(function (offer) {
      statuses[String(offer.gift_variant_id)] = statusFor(cart, offer);
    });
    var detail = { cart: cart, statuses: statuses };
    window.kgGiftSyncState = detail;
    document.dispatchEvent(new CustomEvent('kg:gifts-synced', { detail: detail }));
  }

  function refreshCartUi() {
    if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') {
      window.Rebuy.Cart.fetchCart();
    }
  }

  function runReconcile() {
    if (reconciling) { pendingReconcile = true; return; }
    reconciling = true;
    getCart()
      .then(function (cart) {
        return applyFirstAction(cart).then(function (mutated) {
          if (!mutated) announceState(cart);
          return mutated;
        });
      })
      .then(function (mutated) {
        reconciling = false;
        if (mutated) { refreshCartUi(); scheduleReconcile(); }
        else if (pendingReconcile) { pendingReconcile = false; scheduleReconcile(); }
      })
      .catch(function (err) {
        reconciling = false;
        console.error('[kg-gift] reconcile failed', err);
      });
  }

  function scheduleReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runReconcile, RECONCILE_DEBOUNCE_MS);
  }

  function isCartMutation(url) {
    return CART_MUTATION_PATHS.some(function (path) { return url.indexOf(path) !== -1; });
  }

  // Catch every cart change, whoever made it (theme, Rebuy, other apps).
  // Installed once for all contexts — the old per-snippet scripts double-wrapped
  // fetch. Our own writes use nativeFetch, so they bypass this and don't loop.
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = nativeFetch(input, init);
    if (isCartMutation(url)) result.then(function () { scheduleReconcile(); }).catch(function () {});
    return result;
  };

  ['rebuy:cart.change', 'rebuy:cart.add', 'rebuy:ready', 'rebuy:cart.ready'].forEach(function (evt) {
    document.addEventListener(evt, scheduleReconcile);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleReconcile);
  } else {
    scheduleReconcile();
  }
})();
